import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'

export function BetaSignupPage() {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [preferences, setPreferences] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showModal, setShowModal] = useState(false)

  const techOptions = [
    {
      id: 'phone', label: 'Smartphones', icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
          <line x1="12" y1="18" x2="12.01" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    },
    {
      id: 'laptop', label: 'Laptops', icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
          <line x1="8" y1="21" x2="16" y2="21" stroke="currentColor" strokeWidth="2" />
          <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="2" />
        </svg>
      )
    },
    {
      id: 'audio', label: 'Audio', icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M3 18v-6a9 9 0 0 1 18 0v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" stroke="currentColor" strokeWidth="2" />
        </svg>
      )
    },
    {
      id: 'wearables', label: 'Wearables', icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="7" y="6" width="10" height="12" rx="3" ry="3" stroke="currentColor" strokeWidth="2" />
          <path d="M9 2v4m6-4v4m-6 12v4m6-4v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="10" r="1" fill="currentColor" />
          <path d="M10 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      )
    }
  ]

  const togglePreference = (id: string) => {
    setPreferences(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!fullName) {
      toast.error('Please enter your full name')
      return
    }

    if (!email || !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error('Please enter a valid email address')
      return
    }

    if (preferences.length === 0) {
      toast.error('Please select at least one tech interest')
      return
    }

    setIsSubmitting(true)

    try {
      const apiUrl = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000' : '')
      const response = await fetch(`${apiUrl}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName,
          email,
          preferences
        })
      })

      const result = await response.json()

      if (result.success) {
        setShowModal(true)
        setFullName('')
        setEmail('')
        setPreferences([])
      } else {
        toast.error(result.message || 'Registration failed. Please try again.')
      }
    } catch (error) {
      toast.error('Network error. Please check your connection.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-obsidian relative overflow-hidden">
      {/* Ambient Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute top-1/4 -left-48 w-96 h-96 bg-ember/15 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-48 w-96 h-96 bg-ember/10 rounded-full blur-[100px]"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, delay: 2 }}
        />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 px-4 sm:px-6 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <a href="https://jpdz.app" className="flex items-center space-x-2">
            <span className="text-xl sm:text-2xl font-bold text-gradient-ember">
              JPDZ
            </span>
          </a>
          <span className="text-sm sm:text-base text-gray-400">Beta Registration</span>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center space-x-2 bg-ember/10 border border-ember/30 rounded-full px-4 py-2 mb-6">
            <span className="w-2 h-2 bg-ember rounded-full animate-pulse" />
            <span className="text-ember-bright text-sm font-semibold">Beta Launch Coming Soon</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            <span className="text-gradient-ember">Jeopardize</span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto px-4">
            AI-powered product research that actually works. Skip the endless reviews and discover exactly why you'll love what you buy.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="volcanic-glass p-6 sm:p-8 md:p-12"
        >
          <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
            {/* Name Field */}
            <div>
              <label htmlFor="fullName" className="block text-sm font-semibold text-white mb-2 sm:mb-3">
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="input-volcanic"
                placeholder="Enter your full name"
                autoComplete="name"
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-white mb-2 sm:mb-3">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-volcanic"
                placeholder="Enter your email address"
                autoComplete="email"
              />
            </div>

            {/* Tech Preferences */}
            <div>
              <label className="block text-sm font-semibold text-white mb-3 sm:mb-4">
                What tech interests you? (Select all that apply)
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {techOptions.map((option) => (
                  <motion.button
                    key={option.id}
                    type="button"
                    onClick={() => togglePreference(option.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative p-4 sm:p-6 rounded-xl border-2 transition-all duration-300 text-left ${preferences.includes(option.id)
                        ? 'bg-ember/15 border-ember shadow-ember'
                        : 'bg-volcanic border-glass-border hover:border-ember/50'
                      }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`transition-colors ${preferences.includes(option.id) ? 'text-ember-bright' : 'text-gray-400'}`}>
                        {option.icon}
                      </div>
                      <span className="text-white font-semibold">{option.label}</span>
                      {preferences.includes(option.id) && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="ml-auto"
                        >
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-ember-bright">
                            <path d="M16.667 5L7.5 14.167 3.333 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </motion.div>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-molten py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: isSubmitting ? 1 : 1.01 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.99 }}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-3">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Joining...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-3">
                  Join Beta Waitlist
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4 10h12m-6-6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              )}
            </motion.button>
          </form>

          {/* Benefits */}
          <div className="mt-8 sm:mt-10 pt-8 sm:pt-10 border-t border-glass-border">
            <h3 className="text-white font-semibold text-center mb-4 sm:mb-6">What you get:</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {[
                'Early access to JPDZ beta',
                'Exclusive product insights',
                'Shape the product roadmap',
                'No spam, just updates'
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-ember-bright flex-shrink-0 mt-0.5">
                    <path d="M16.667 5L7.5 14.167 3.333 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-12 border-t border-glass-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
            <div>
              <span className="text-2xl font-bold text-gradient-ember">JPDZ</span>
              <p className="text-gray-400 mt-2">Fall in love with tech again.</p>
            </div>

            <a href="mailto:ratnam@jpdz.app" className="text-gray-400 hover:text-ember-bright transition-colors">
              Contact
            </a>

            <div className="flex space-x-4">
              <a href="https://www.linkedin.com/company/jpdz/" target="_blank" rel="noopener noreferrer" className="p-3 bg-volcanic border border-glass-border rounded-lg hover:border-ember/50 transition-colors group">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-gray-400 group-hover:text-ember-bright transition-colors">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor" />
                </svg>
              </a>
              <a href="https://www.instagram.com/jpdz.app/" target="_blank" rel="noopener noreferrer" className="p-3 bg-volcanic border border-glass-border rounded-lg hover:border-ember/50 transition-colors group">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-gray-400 group-hover:text-ember-bright transition-colors">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="currentColor" strokeWidth="2" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" stroke="currentColor" strokeWidth="2" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </a>
              <a href="https://x.com/JeopardizeP" target="_blank" rel="noopener noreferrer" className="p-3 bg-volcanic border border-glass-border rounded-lg hover:border-ember/50 transition-colors group">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-gray-400 group-hover:text-ember-bright transition-colors">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor" />
                </svg>
              </a>
            </div>
          </div>

          <div className="text-center mt-8 text-gray-500 text-sm">
            &copy; 2025 JPDZ. Made with ❤️ for tech lovers.
          </div>
        </div>
      </footer>

      {/* Success Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-obsidian/90 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="volcanic-glass p-12 max-w-md w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="w-20 h-20 bg-gradient-to-r from-ember to-ember-dim rounded-full flex items-center justify-center mx-auto mb-6 shadow-ember-lg"
              >
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <path d="M33.333 10L15 28.333 6.667 20" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.div>

              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">Welcome to JPDZ Beta! 🎉</h3>
              <p className="text-base sm:text-lg text-gray-300 mb-6 sm:mb-8">
                Thanks for joining! You'll receive a confirmation email shortly with next steps. Get ready to fall in love with tech again.
              </p>

              <motion.button
                onClick={() => setShowModal(false)}
                className="btn-molten px-8 py-3"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Continue
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
